import { useState, useCallback } from 'react';
import { BookOpen, Search, X } from 'lucide-react';
import type { BookInfo } from '../services/googleBooksService';
import { searchBooks, searchByISBN, formatAuthors, formatPublishedDate } from '../services/googleBooksService';
import './BookLookup.css';

interface BookLookupProps {
  onSelect: (book: BookInfo) => void;
  onClose: () => void;
  onSkip?: () => void; // Optional: create blank source without book data
  initialQuery?: string;
}

export default function BookLookup({ onSelect, onClose, onSkip, initialQuery = '' }: BookLookupProps) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<BookInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<'general' | 'isbn'>('general');

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);

    try {
      let books: BookInfo[];

      if (searchType === 'isbn') {
        const book = await searchByISBN(query);
        books = book ? [book] : [];
      } else {
        books = await searchBooks(query);
      }

      setResults(books);

      if (books.length === 0) {
        setError('No books found. Try a different search term.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search books');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, searchType]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectBook = (book: BookInfo) => {
    onSelect(book);
    // Don't call onClose - let parent handle navigation/closing
  };

  return (
    <div className="book-lookup-overlay" onClick={onClose}>
      <div className="book-lookup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="book-lookup-header">
          <h2><BookOpen size={20} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.5rem' }} /> Book Lookup</h2>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="book-lookup-search">
          <div className="search-type-toggle">
            <button
              className={`toggle-btn ${searchType === 'general' ? 'active' : ''}`}
              onClick={() => setSearchType('general')}
            >
              Title/Author
            </button>
            <button
              className={`toggle-btn ${searchType === 'isbn' ? 'active' : ''}`}
              onClick={() => setSearchType('isbn')}
            >
              ISBN
            </button>
          </div>

          <div className="search-input-row">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchType === 'isbn' ? 'Enter ISBN (10 or 13 digits)...' : 'Search by title, author, or keywords...'}
              className="search-input"
              autoFocus
            />
            <button
              onClick={handleSearch}
              disabled={loading || !query.trim()}
              className="search-btn"
            >
              {loading ? '...' : <Search size={18} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="book-lookup-error">
            {error}
          </div>
        )}

        <div className="book-lookup-results">
          {loading && (
            <div className="loading-state">Searching...</div>
          )}

          {!loading && results.length > 0 && (
            <div className="results-list">
              {results.map((book) => (
                <div
                  key={book.id}
                  className="book-result"
                  onClick={() => handleSelectBook(book)}
                >
                  <div className="book-thumbnail">
                    {book.thumbnail ? (
                      <img src={book.thumbnail} alt={book.title} />
                    ) : (
                      <div className="no-thumbnail"><BookOpen size={32} /></div>
                    )}
                  </div>
                  <div className="book-info">
                    <div className="book-title">{book.title}</div>
                    {book.subtitle && (
                      <div className="book-subtitle">{book.subtitle}</div>
                    )}
                    <div className="book-author">{formatAuthors(book.authors)}</div>
                    <div className="book-meta">
                      {book.publisher && <span>{book.publisher}</span>}
                      {book.publishedDate && (
                        <span>{formatPublishedDate(book.publishedDate)}</span>
                      )}
                      {book.pageCount && <span>{book.pageCount} pages</span>}
                    </div>
                    {book.isbn13 && (
                      <div className="book-isbn">ISBN: {book.isbn13}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && results.length === 0 && !error && (
            <div className="empty-state">
              <div className="empty-icon"><BookOpen size={48} /></div>
              <p>Search for a book by title, author, or ISBN</p>
              <p className="hint">Results from Google Books</p>
              {onSkip && (
                <button className="skip-btn" onClick={onSkip}>
                  Skip - Create blank source
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
